<template>
  <div id="login" :class="{mobile: isMobile}">

    <div class="middle">
      <div class="middle__backdrop">{{ $t("welcome_to_ishopfood") }}</div>
      <div class="middle__container">
        <div class="container__left">
          <div class="container__title">{{ $t("login") }}</div>
          <div class="container__description">{{ $t("welcome_description") }}</div>
          <div>
            <label for="txtEmail">{{ $t("user.email") }}</label>
            <input id="txtEmail" type="text" v-model="username" @keyup.enter="loginAction">
            <label for="txtPassword">{{ $t("user.password") }}</label>
            <input id="txtPassword" type="password" v-model="password" @keyup.enter="loginAction">
            <template v-if="showRegionSelector">
              <label for="selRegion">{{ $t("loginPage.region") }}</label>
              <select id="selRegion" v-model="region" class="region-select">
                <option value="">{{ $t("loginPage.region_default") }}</option>
                <option value="na0">{{ $t("loginPage.region_na0") }}</option>
                <option value="na1">{{ $t("loginPage.region_na1") }}</option>
              </select>
            </template>
            <div class="container__remember">
              <div class="grow">
                <input type="checkbox" id="chkRemember" v-model="rememberMe">
                <label for="chkRemember">{{ $t("remember_me") }}</label>
              </div>
              <a href="javascript:void(0)" class="link" @click="showForgotPassword">{{ $t("forgot_password") }}</a>
            </div>
            <div class="container__buttons">
              <button class="btn btn-primary" @click="loginAction">{{ $t("login") }}</button>
            </div>
          </div>
        </div>
        <div class="container__right">
          <img src="@/assets/img/spinner-white.png" width="160" height="160">
        </div>
      </div>
    </div>

    <LoginBottom/>

    <Modal
      name="forgot-password"
      dismissable
      :title="$t('forgot_password')"
      :actions="[{name: $t('back'), action: 'close'}, {name: $t('send'), action: sendForgotPassword, primary: true}]">
      <p>
        {{ $t('forgot_password_text') }}
      </p>
      <input type="text" class="w-100" v-model="forgotEmail">
    </Modal>

    <Modal
      name="login-error"
      :title="$t('error.login')"
      :actions="[{name: $t('back'), action: 'close'}]">
      {{ errorMessage }}
    </Modal>

    <Modal name="forgot-password-confirmation" :title="$t('forgot_password_confirmation')" :actions="[{name: $t('back'), action: 'close'}]">
      <p>
        {{ $t('forgot_password_confirmation_text') }}
      </p>
    </Modal>

    <div class="receptionError" :class="{ visible: receptionErrorVisible }">
      <div class="receptionError__icon">
        <svgicon src="icon/error.svg" :width="64" :height="64"></svgicon>
      </div>
      <div class="receptionError__text" v-html="$t('loginPage.invalid_user_message')"></div>
    </div>

  </div>
</template>

<script>
  import { Core } from '@/core/Core';
  import Tooltip from '@/components/tools/Tooltip.vue';
  import { Validate } from "@/resources/validate";
  import LoginTop from "@/components/login/Top.vue";
  import LoginBottom from "@/components/login/Bottom.vue";
  import { mapActions } from 'vuex';
  import Vue from "vue";
  import VueIntercom from "vue-intercom";

  export default {

    name: "login",

    components: { Tooltip, LoginTop, LoginBottom },

    data() {
      return {
        username: "",
        password: "",
        region: "",
        rememberMe: true,
        errorMessage: "",
        forgotEmail: "",
        trainingTermsId: "video_training",
        trainingCompleted: false,
        receptionErrorVisible: false
      }
    },

    computed: {
      redirect() {
        let redirect = decodeURIComponent(this.$route.query.redirect || "");
        if (redirect.toLowerCase().indexOf("login") > -1) {
          return "";
        }
        return redirect;
      },
      userStatusHasMandatoryTraining() {
        return this.$store.state.loggedUser.isBranchAdmin || this.$store.state.loggedUser.isCompanyAdmin;
      },
      isMobile() {
        return this.$mq === 'xs' || this.$mq === 'sm' || this.$mq === 'md';
      },
      // Only expose the NA0/NA1 selector when the build is pointed at a
      // prod-style API host. In dev / localhost the regions don't apply.
      showRegionSelector() {
        let url = (typeof Parameters !== "undefined" && Parameters.urlServer) || "";
        return /(^|\/\/)(?:[^\/]*\.)?ishopfood\.com(?::|\/|$)/i.test(url);
      }
    },

    mounted() {
      if (this.redirect) {
        toast(this.$t("error.please_login"), "warning");
      }
    },

    methods: {
      ...mapActions(["login", "forgotPassword"]),

      focusOut() {
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
      },

      async loginAction() {
        this.focusOut();

        this.$store.commit("showSpinner", true);
        let response = await this.login({
          superAdminToken: Parameters.superAdminToken,
          username: this.username,
          password: this.password,
          region: this.region
        });
        this.$store.commit("showSpinner", false);
        if (response instanceof Core.Error) {
          this.errorMessage = response.status === 401 ? this.$t("error.wrong_email_password") : this.$t("error.no_server");
          this.$modal.show("login-error");
          return;
        }

        // Only reception users can login in the standalone version
        if (typeof nw !== "undefined" && !response.isReceptionUser) {
          this.receptionErrorVisible = true;
          return;
        }

        this.$store.commit('setContextServer', response.homeServer);
        this.$store.commit('logIn', {
          id: response.id,
          token: response.token,
          isSuperAdmin: response.isSuperAdmin,
          isDistributor: response.isDistributor,
          isBranchAdmin: response.isBranchAdmin,
          isCompanyAdmin: response.isShopAdmin,
          isClerkUser: response.isClerkUser,
          isReceptionUser: response.isReceptionUser,
          isPosUser: response.isPosUser,
          info: response.info,
          branch: response.branch,
          company: response.shop,
          permission: response.permission,
          homeServer: response.homeServer,
          accessibleServers: response.accessibleServers || null,
          accessibleBranches: response.accessibleBranches || null,
          user: {
            email: response.user && response.user.user ? response.user.user.email : "",
            firstName: response.user && response.user.user ? response.user.user.firstName : "",
            lastName: response.user && response.user.user ? response.user.user.lastName : ""
          }
        });
        this.$store.commit('tokenExpiration');
        this.persistUserConfig(response);

        if (this.userStatusHasMandatoryTraining) {
          await this.verifyTrainingConfirmation();
          this.$store.state.loggedUser.trainingCompleted = this.trainingCompleted;
          if (!this.trainingCompleted) {
            this.redirectUserToTraining();
            return;
          }
        } else {
          this.$store.state.loggedUser.trainingCompleted = true;
        }

        //Init Intercom
        if(Parameters.intercomAppId) {
          Vue.use(VueIntercom, { appId: Parameters.intercomAppId });
          this.$intercom.boot({
            user_id: this.$store.state.loggedUser.id,
            name: this.$store.state.loggedUser.user.firstName,
            email: this.$store.state.loggedUser.user.email,
          });
          this.$intercom.show();
        }

        if (this.redirect) {
          this.$router.push(this.redirect);
          return;
        }

        let defaultRoute = this.$store.getters.defaultRouting;
        if (defaultRoute) {
          await this.$router.push(defaultRoute);
        }
        //this.showLaunchPopup();
      },

      showLaunchPopup() {
        if (!getCookie("new-ui-launch-popup-2")) {
          setCookie("new-ui-launch-popup-2", 1, new Date(2023, 12, 31) );
          this.$modal.show("new-ui");
        }
      },

      persistUserConfig(response) {
        if (typeof nw === "undefined" || !Parameters.userConfigPath) {
          return;
        }
        let fs = nw.require("fs");
        fs.writeFile(Parameters.userConfigPath, response.token, (err) => {
          if (err) throw err;
          console.log('The file has been saved!');
        });
      },

      showForgotPassword() {
        this.forgotEmail = "";
        this.$modal.show("forgot-password");
      },

      async sendForgotPassword() {
        if (!Validate.email(this.forgotEmail)) {
          this.errorMessage = this.$t("error.invalid_email");
          this.$modal.show("login-error");
          return;
        }
        this.$store.commit("showSpinner", true);
        let response = await this.forgotPassword({
          email: this.forgotEmail,
          locale: this.$ts.state.locale,
          redirect_url: location.href.replace("login", "reset_password?token={TOKEN}")
        });
        if (response instanceof Core.Error) {
          this.$modal.show("forgot-password", false);
          this.errorMessage = this.$t("error.no_server");
          this.$modal.show("login-error");
          this.$store.commit("showSpinner", false);
        } else {
          this.$modal.show("forgot-password", false);
          this.$store.commit("showSpinner", false);
          this.$modal.show("forgot-password-confirmation");
        }
      },

      async verifyTrainingConfirmation() {
        try {
          let response = await Core.request('GET', '/api/me/terms_agreements', {termsId: this.termsId});
          if (response && response.data) {
            this.trainingCompleted = !!response.data[0];
          } else {
            this.trainingCompleted = false;
            toast(this.$t("error.no_server"), "error");
            console.log(response);
          }
        } catch (e) {
          this.trainingCompleted = false;
          toast(this.$t("error.no_server"), "error");
          console.log(e)
        }
      },
      redirectUserToTraining() {
        let user = this.$store.state.loggedUser;
        let branch = user.branch || undefined;
        let company = user.company || undefined;
        if (user.isBranchAdmin) {
          this.$router.push({ path: '/training', query: {branch}});
        } else if (user.isCompanyAdmin) {
          this.$router.push({ path: '/training', query: {branch, company}});
        }
      },
      setLocale(language) {
        this.$ts.commit("setLocale", language);
      }
    }
  };

</script>

<style lang="scss" scoped>

  #login {
    display: flex;
    flex-direction: column;
    padding: 0;
    height: 100%;
    &.mobile {
      display: block;
      overflow: hidden;
      .middle {
        height: 100%;
        display: block;
      }
      .middle__backdrop {
        display: none;
      }
      .middle__container {
        width: 100%;
        height: 100%;
        flex-direction: column-reverse;
        border-radius: 0;
        top: 0;
        left: 0;
        transform: none;
        background: linear-gradient(0deg, #ff8a80 0%, #F44643 100%);
        .container__left,
        .container__right {
          border-radius: 0;
          width: 100%;
        }
        .container__left {
          border-top-left-radius: 80px;
          padding-bottom: 40px;
        }
        .container__right {
          border-bottom-right-radius: 80px;
          background: none;
          position: relative;
          &::after {
            content: "";
            position: absolute;
            right: 0;
            bottom: 0px;
            width: 80px;
            height: 200px;
            background: white;
            background-color: transparent;
            border-bottom-right-radius: 80px;
            box-shadow: 4px 70px 0px white;
          }
        }
      }
    }
  }

  label {
    font-size: 16px;
  }

  .middle {
    flex-grow: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .middle__backdrop {
    border: 4px solid #E6E6E6;
    padding: 48px;
    border-radius: 48px;
    font-size: 40px;
    color: #DBDBDB;
    font-weight: bold;
    width: 40%;
    height: 60%;
    position: absolute;
    top: 46%;
    left: 37%;
    transform: translate(-50%, -50%);
    white-space: nowrap;
    min-height: 600px;
    min-width: 600px;
  }

  .middle__container {
    background-color: white;
    border-radius: 48px;
    display: flex;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: linear-gradient(0deg , #F8584A 0%, #F44643 50%, white 51%, white 100%);
    box-shadow: 0 2px 24px 1px rgba(0, 0, 0, 0.1);
    .container__left,
    .container__right {
      border-radius: 45px;
      flex-grow: 1;
      width: 360px;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }
    .container__left {
      padding: 40px 48px;
      background-color: white;
    }
    .container__right {
      padding: 0 48px;
      background: linear-gradient(0deg, #F8584A 0%, #F0343B 100%);
      align-items: center;
    }
    .container__title {
      font-size: 20px;
      font-weight: bold;
      color: #404040;
      margin-bottom: 8px;
    }
    .container__description {
      font-size: 12px;
      color: #808080;
      margin-bottom: 48px;
    }
    .container__remember {
      display: flex;
      align-items: center;
      margin-bottom: 36px;
      .grow {
        flex-grow: 1;
      }
      label {
        font-size: 12px;
        color: #808080;
        padding-left: 4px;
        margin-bottom: 0;
      }
      .link {
        font-size: 12px;
        color: #FC6650;
        text-align: right;
      }
    }
    .container__buttons {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      .buttons__languages {
        flex-grow: 1;
        display: flex;
        font-size: 12px;
        color: #808080;
        .languages__language {
          padding: 8px 12px 8px 0;
        }
      }
    }
  }

  label {
    font-size: 12px;
    color: #808080;
    margin-bottom: 4px;
  }

  input {
    margin-bottom: 18px;
  }

  .region-select {
    width: 100%;
    margin-bottom: 18px;
    padding: 6px 8px;
    border: 1px solid #DBDBDB;
    border-radius: 4px;
    background-color: white;
    color: #404040;
    font-size: 14px;
  }

  form {
    display: flex;
    flex-direction: column;
  }

  button.btn {
    padding: 10px 20px;
    height: 40px;
    border-radius: 20px;
  }

  .receptionError {
    background-color: #feb055;
    position: fixed;
    width: calc(100% - 40px);
    max-width: 640px;
    left: 50%;
    transform: translateX(-50%);
    padding: 20px;
    border-radius: 20px;
    color: white;
    display: flex;
    align-items: center;
    opacity: 0;
    top: -20px;
    transition: opacity 0.5s ease-in-out, top 0.5s ease-in-out;
    &.visible {
      opacity: 1;
      top: 20px;
    }
    .receptionError__icon {
      margin-right: 20px;
      fill: white;
    }
  }

</style>
